import { Box, BoxProps } from '@nominex/dex-uikit'
import get from 'lodash-es/get'
import React, { cloneElement, isValidElement, useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

type AnimationType = 'rotate' | 'scale'

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`

const scale = keyframes`
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
`

const rotateAnimation = css`
  animation: ${rotate} 0.6s cubic-bezier(0.59, -0.45, 0.31, 1.46);
`

const scaleAnimation = css`
  animation: ${scale} 0.5s cubic-bezier(0.5, 0, 0.5, 1);
`

const AnimatedIconWrapper = styled(Box)`
  position: relative;
  z-index: 10;
  height: 0;
  width: 38px;
`

const AnimatedIconTrigger = styled.button<{
  clicked: boolean
  clickable: boolean
  background?: string
  borderColor?: string
  animation: AnimationType
}>`
  position: absolute;
  left: 0;
  top: -18px;
  border: 3px solid;
  color: inherit;
  border-color: ${({ theme, borderColor }) => {
    if (typeof borderColor !== 'string') {
      return theme.colors.backgroundAlt
    }

    return get(theme.colors, borderColor, borderColor)
  }};
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme, background }) => {
    if (typeof background !== 'string') {
      return theme.colors.gradients.accent
    }

    return get(theme.colors, background, background)
  }};
  border-radius: 19px;
  cursor: ${({ clickable }) => clickable && 'pointer'};

  & > * {
    ${({ clicked, animation }) => clicked && animation === 'rotate' && rotateAnimation};
    fill: ${({ theme }) => theme.colors.placeholder};
    color: currentColor;
  }

  & {
    ${({ clicked, animation }) => clicked && animation === 'scale' && scaleAnimation};
  }

  & path {
    transition: fill 0.2s ease;
  }

  &:hover > * {
    fill: ${({ clickable, theme }) => clickable && theme.colors.text};
  }
`

interface AnimatedIconProps extends BoxProps {
  background?: string
  borderColor?: string
  animation?: AnimationType
  children: React.ReactNode
  clickable?: boolean
  onClick?: () => any
  trigger?: any
}

export default function AnimatedIcon({
  children,
  clickable,
  onClick,
  animation = 'rotate',
  trigger,
  background,
  borderColor,
  ...props
}: AnimatedIconProps) {
  const [clicked, setstate] = useState(false)

  useEffect(() => {
    const timer = clicked ? setTimeout(() => setstate(false), 600) : undefined

    return () => timer && clearTimeout(timer)
  }, [clicked])

  useEffect(() => {
    setstate(true)
  }, [trigger])

  return (
    <AnimatedIconWrapper {...props}>
      <AnimatedIconTrigger
        onClick={() => {
          if (onClick) {
            if (clickable) {
              setstate(true)
            }
            onClick()
          }
        }}
        animation={animation}
        clicked={clicked}
        clickable={clickable}
        background={background}
        borderColor={borderColor}
      >
        {isValidElement(children) && cloneElement(children)}
      </AnimatedIconTrigger>
    </AnimatedIconWrapper>
  )
}
